import React, { useEffect, useRef } from "react";
import useFetch from "../hooks/useFetch";
import { ReactComponent as HeaderBanner } from "../css/images/IX.svg";
import { noWidow } from "../js/typography";


export default function HeaderHomePage({ onOpen }) {
  const contactsFetch = useFetch(`${window.$host}/api/contact?populate=*`);
  const contentFetch = useFetch(`${window.$host}/api/hero`);
  const headerBgRef = useRef(null);
  const headerBoxRef = useRef(null);

  useEffect(() => {
    if (
      contentFetch.loading ||
      contactsFetch.loading ||
      contentFetch.error ||
      contactsFetch.error
    ) {
      return;
    }
    const container = headerBgRef.current;
    const eventTarget = headerBoxRef.current;
    if (!container) {
      return;
    }

    const activeTarget = eventTarget || window;

    const svg = container.querySelector("svg");
    if (!svg) {
      return;
    }

    const points = Array.from(svg.querySelectorAll(".scatter"));
    if (!points.length) {
      return;
    }

    const svgPoint = svg.createSVGPoint();
    const radius = 70;
    const maxOffset = 500;
    const ease = 0.24;
    const returnEase = 0.05;
    const scatterEase = 0.04;
    const reassembleEase = 0.012;
    const noiseAmount = 0.75;

    const data = points.map(el => {
      const box = el.getBBox();
      return {
        el,
        cx: box.x + box.width / 2,
        cy: box.y + box.height / 2,
        x: 0,
        y: 0,
        tx: 0,
        ty: 0,
        noiseAngle: (Math.random() * 2 - 1) * noiseAmount
      };
    });

    let rafId = 0;
    let isRunning = false;
    let reassembleTimer = 0;
    let scatterLock = false;
    let phase = "idle";

    const getMousePosition = event => {
      const ctm = svg.getScreenCTM();
      if (!ctm) return null;
      svgPoint.x = event.clientX;
      svgPoint.y = event.clientY;
      return svgPoint.matrixTransform(ctm.inverse());
    };

    const setTargets = mouse => {
      data.forEach(item => {
        if (!mouse) {
          item.tx = 0;
          item.ty = 0;
          return;
        }
        const dx = mouse.x - item.cx;
        const dy = mouse.y - item.cy;
        const dist = Math.hypot(dx, dy);

        if (dist < radius && dist > 0) {
          const force = (1 - dist / radius) ** 2 * maxOffset;
          const angle = Math.atan2(dy, dx) + item.noiseAngle;
          item.tx = -Math.cos(angle) * force;
          item.ty = -Math.sin(angle) * force;
          return;
        }

        item.tx = 0;
        item.ty = 0;
      });
    };

    const onMove = event => {
      if (scatterLock) return;
      const point = getMousePosition(event);
      if (!point) return;
      setTargets(point);
      start();
    };

    const onLeave = () => {
      if (scatterLock) return;
      setTargets(null);
      start();
    };

    // Клик по первому экрану: хаотичный разлёт всех точек и возврат в логотип.
    // Клики по кнопкам/ссылкам не запускают эффект — они работают как обычно.
    const scatterAndReassemble = event => {
      if (event && event.target.closest("button, a")) return;
      scatterLock = true;
      phase = "scatter";
      const spread = Math.max(window.innerWidth, window.innerHeight);
      data.forEach(item => {
        const angle = Math.random() * Math.PI * 2;
        const dist = 150 + Math.random() * spread * 0.7;
        item.tx = Math.cos(angle) * dist;
        item.ty = Math.sin(angle) * dist;
      });
      start();
      window.clearTimeout(reassembleTimer);
      reassembleTimer = window.setTimeout(() => {
        scatterLock = false;
        phase = "reassemble";
        setTargets(null);
        start();
      }, 900);
    };

    const tick = () => {
      let hasMotion = false;

      data.forEach(item => {
        let currentEase;
        if (phase === "scatter") {
          currentEase = scatterEase;
        } else if (phase === "reassemble") {
          currentEase = item.tx === 0 && item.ty === 0 ? reassembleEase : ease;
        } else {
          currentEase = item.tx === 0 && item.ty === 0 ? returnEase : ease;
        }
        item.x += (item.tx - item.x) * currentEase;
        item.y += (item.ty - item.y) * currentEase;

        if (Math.abs(item.tx - item.x) > 0.05 || Math.abs(item.ty - item.y) > 0.05) {
          hasMotion = true;
        }

        item.el.style.transform = `translate(${item.x}px, ${item.y}px)`;
      });

      if (hasMotion) {
        rafId = window.requestAnimationFrame(tick);
        return;
      }

      isRunning = false;
      if (phase === "reassemble") {
        phase = "idle";
        scatterLock = false;
      }
    };

    const start = () => {
      if (isRunning) return;
      isRunning = true;
      rafId = window.requestAnimationFrame(tick);
    };

    activeTarget.addEventListener("mousemove", onMove);
    activeTarget.addEventListener("mouseleave", onLeave);
    activeTarget.addEventListener("click", scatterAndReassemble);

    return () => {
      activeTarget.removeEventListener("mousemove", onMove);
      activeTarget.removeEventListener("mouseleave", onLeave);
      activeTarget.removeEventListener("click", scatterAndReassemble);
      window.clearTimeout(reassembleTimer);
      window.cancelAnimationFrame(rafId);
      isRunning = false;
      data.forEach(item => {
        item.el.style.transform = "";
      });
    };
  }, [
    contentFetch.loading,
    contactsFetch.loading,
    contentFetch.error,
    contactsFetch.error
  ]);


  if (contentFetch.loading || contactsFetch.loading) return;
  if (contentFetch.error || contactsFetch.error) return;

  return (
    <header className="header bg-blue">
      <div className="container">
        <div className="h-100 header__box" ref={headerBoxRef}>
          <div className="header-bg" ref={headerBgRef}>
            <HeaderBanner className="header-bg__img" />
          </div>
          <div className="header-top">
            <div className="header-top__logo">
              <img
                src={window.$host + contactsFetch.data.data.Logo.url}
                alt={contactsFetch.data.data.Logo.alternativeText}
              />
            </div>
            <div className="header-top__actions">
              <button
                className="dark-arrow"
                onClick={() => {
                  onOpen(true);
                }}
              >
                <span className="dark-arrow__text">Запустить продукт</span>
              <span className="dark-arrow__arrow">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="17"
                  height="12"
                  viewBox="0 0 17 12"
                  fill="none"
                >
                  <circle
                    cx="4"
                    cy="6"
                    r="1"
                    transform="rotate(90 4 6)"
                    fill="white"
                  />
                  <circle
                    cx="1"
                    cy="6"
                    r="1"
                    transform="rotate(90 1 6)"
                    fill="white"
                  />
                  <circle
                    cx="7"
                    cy="6"
                    r="1"
                    transform="rotate(90 7 6)"
                    fill="white"
                  />
                  <circle
                    cx="10"
                    cy="6"
                    r="1"
                    transform="rotate(90 10 6)"
                    fill="white"
                  />
                  <circle
                    cx="13"
                    cy="6"
                    r="1"
                    transform="rotate(90 13 6)"
                    fill="white"
                  />
                  <circle
                    cx="16"
                    cy="6"
                    r="1"
                    transform="rotate(90 16 6)"
                    fill="white"
                  />
                  <circle
                    cx="13"
                    cy="9"
                    r="1"
                    transform="rotate(90 13 9)"
                    fill="white"
                  />
                  <circle
                    cx="13"
                    cy="3"
                    r="1"
                    transform="rotate(90 13 3)"
                    fill="white"
                  />
                  <circle
                    cx="10"
                    cy="0.500001"
                    r="0.5"
                    transform="rotate(90 10 0.500001)"
                    fill="white"
                  />
                  <circle
                    cx="10"
                    cy="11.5"
                    r="0.5"
                    transform="rotate(90 10 11.5)"
                    fill="white"
                  />
                </svg>
              </span>
              </button>
            </div>
          </div>
          <div className="header-hero text-white">
            <div className="header-hero__empty"></div>
            <h1 className="title-1 header-hero__title">
              {noWidow(contentFetch.data.data.Title)}{" "}
              <span className="text-purple">
                {noWidow(contentFetch.data.data.TitlePurple)}
              </span>
            </h1>
            <p className="text-20 header-hero__text">
              {noWidow(contentFetch.data.data.Text)}{" "}
              <span className="text-purple">
                {noWidow(contentFetch.data.data.TextPurple)}
              </span>
            </p>
          </div>
        </div>
      </div>
    </header>
  );
}
